import { BoardColumnShell } from '@/components/board/board-column-shell';
import { cn } from '@/lib/utils';
import type { PipelineStageBucket, PipelineStageType } from '@/types/recruitment';
import { useDroppable } from '@dnd-kit/core';
import { type ReactNode } from 'react';

const STAGE_CATEGORY_COLORS: Record<PipelineStageType, string> = {
  SYSTEM: '#6B7280',
  OUTREACH: '#3B82F6',
  INTERVIEW: '#8B5CF6',
  ASSESSMENT: '#F59E0B',
  OFFER: '#10B981',
  CUSTOM: '#64748B',
};

interface PipelineColumnProps {
  stage: PipelineStageBucket;
  count: number;
  children: ReactNode;
}

export function PipelineColumn({ stage, count, children }: PipelineColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const categoryColor = STAGE_CATEGORY_COLORS[stage.type] ?? STAGE_CATEGORY_COLORS.CUSTOM;

  return (
    <BoardColumnShell
      accentColor={categoryColor}
      header={
        <>
          <div
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: categoryColor }}
          />
          <span
            className="truncate font-sans text-[13px] font-medium text-foreground"
            title={stage.name}
          >
            {stage.name}
          </span>
          <span className="tabular-nums text-xs text-muted-foreground">{count}</span>
        </>
      }
      bodyRef={setNodeRef}
      bodyClassName={cn(
        'transition-[background-color,box-shadow] duration-200 ease-out',
        isOver && 'bg-accent/40',
      )}
    >
      {count === 0 ? (
        <div className="flex h-20 items-center justify-center rounded-md border border-dashed border-border/50 text-[11px] text-muted-foreground/60">
          No candidates
        </div>
      ) : (
        children
      )}
    </BoardColumnShell>
  );
}
